"use client";

import { TypeModalWithoutUnresolvableLinksResponse } from "@2u-landing-pages/types";
import { useCallback, useEffect, useRef, useState } from "react";

interface ModalState {
  isOpen: boolean;
  activeModalId: string | null;
  activeModal: TypeModalWithoutUnresolvableLinksResponse | null;
}

interface UseModalManagerProps {
  modals: TypeModalWithoutUnresolvableLinksResponse[];
}

export const useModalManager = ({ modals }: UseModalManagerProps) => {
  const [modalState, setModalState] = useState<ModalState>({
    isOpen: false,
    activeModalId: null,
    activeModal: null,
  });

  const scrollTriggeredRef = useRef<boolean>(false);
  const hasTriggeredRef = useRef<boolean>(false);
  const openModalRef = useRef<(modalId: string) => void>();
  const delayTimeoutSetRef = useRef<boolean>(false);



  // Open modal by ID
  const openModal = useCallback(
    (modalId: string) => {
      setModalState(prevState => {
        // Check if a modal is already open - if so, fail silently
        if (prevState.isOpen) {
          return prevState;
        }

        // Find modal directly
        const modal = modals.find((modal) => modal.fields.modal_id === modalId);

        if (modal) {
          return {
            isOpen: true,
            activeModalId: modalId,
            activeModal: modal,
          };
        }

        return prevState;
      });
    },
    [modals]
  );

  // Store the latest openModal function in a ref
  openModalRef.current = openModal;

  // Close modal
  const closeModal = useCallback(() => {
    setModalState({
      isOpen: false,
      activeModalId: null,
      activeModal: null,
    });
    // Reset scroll trigger flag when modal is closed
    scrollTriggeredRef.current = false;
    // Reset hasTriggeredRef when modal is closed so it can be triggered again
    hasTriggeredRef.current = false;
  }, []);

  // Simple effect for debug override
  useEffect(() => {
    if (modals.length === 0) return;

    const modal = modals.find(m => m.fields.debug_override === "True");
    if (modal) {
      openModal(modal.fields.modal_id);
    }
  }, [modals, openModal]);

  // Auto-open with delay - only set up once per page load
  if (modals.length > 0 && !delayTimeoutSetRef.current) {
    const modal = modals.find(m => m.fields.auto_open_modal && m.fields.delay_open_ms);
    if (modal) {
      delayTimeoutSetRef.current = true; // Mark as set up, never reset
      hasTriggeredRef.current = true;
      setTimeout(() => {
        openModal(modal.fields.modal_id);
      }, modal.fields.delay_open_ms);
    }
  }

  // Simple effect for scroll trigger
  useEffect(() => {
    if (modals.length === 0) return;

    const modal = modals.find(m => m.fields.scroll_depth_to_open);
    if (modal && !scrollTriggeredRef.current) {
      const handleScroll = () => {
        const scrollPercent = (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100;
        if (modal.fields.scroll_depth_to_open && scrollPercent >= modal.fields.scroll_depth_to_open) {
          scrollTriggeredRef.current = true;
          openModal(modal.fields.modal_id);
          window.removeEventListener("scroll", handleScroll);
        }
      };

      window.addEventListener("scroll", handleScroll, { passive: true });
      return () => window.removeEventListener("scroll", handleScroll);
    }
  }, [modals, openModal]);


  return {
    isOpen: modalState.isOpen,
    activeModalId: modalState.activeModalId,
    activeModal: modalState.activeModal,
    openModal,
    closeModal,
  };
};
